import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Button, Card, ShareMenu, useExport, useToast } from '@kss/ui';
import {
  ArrowLeft,
  Download,
  MessageCircle,
  Printer,
  RefreshCw,
} from 'lucide-react';
import {
  invoiceService,
  invoiceItemService,
  companyService,
  customerService,
  printFormatService,
  invoicePrintSnapshotService,
  entityTranslationService,
  translationIndex,
  type Invoice,
  type InvoiceItem,
  type Company,
  type Customer,
  type PrintFormat,
} from '@kss/api';
import { decodeId } from '../../utils/idCodec';
import { toAbsoluteImageUrl } from '../../utils/persistImage';
import { invoiceUrl, isUnreachableHost } from '../../utils/storefrontLinks';
import InvoiceDocument from './InvoiceDocument';

/**
 * Panel-side invoice preview page. Fetches the invoice header +
 * line items + buyer + seller, then delegates the visual layout to
 * the shared `InvoiceDocument` component so the panel view and the
 * customer view can never drift. Bug ref: AI Agent 2026-05-14 —
 * "invoice print download, what's up share not available and invoice
 * format should be same"; SSO ISSUE 4 — "customer-facing invoices
 * section displays invoices incorrectly".
 *
 * The toolbar above the document carries: Print, Download (PDF),
 * WhatsApp Share. The document body is hidden from print via the
 * `@media print` rule below (which targets the `.invoice-doc` class
 * emitted by `InvoiceDocument`).
 */
export default function InvoicePreview() {
  const { id: rawId } = useParams<{ id?: string }>();
  const id = decodeId(rawId);
  const navigate = useNavigate();
  const toast = useToast();
  const { exportToPdf } = useExport();
  const docRef = useRef<HTMLDivElement>(null);

  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [items, setItems] = useState<InvoiceItem[]>([]);
  const [seller, setSeller] = useState<Company | null>(null);
  const [buyer, setBuyer] = useState<Customer | null>(null);
  /** Tenant's saved print template; null until loaded, `{}` when none configured. */
  const [format, setFormat] = useState<PrintFormat | null>(null);
  /* Product-name translations for the print language. Empty for an
     untranslated tenant, which prints the stored names — the correct
     behaviour, so this never blocks the render. */
  const [productTranslations, setProductTranslations] = useState<Map<string, string>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<'print' | 'pdf' | 'wa' | null>(null);

  const load = async () => {
    if (!id || id === 'new') {
      setError('Invoice id is missing.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const inv = await invoiceService.get(id);
      setInvoice(inv);
      // Line items + buyer + seller in parallel — none of them blocks
      // the document render (each renders an empty state on failure).
      const tasks = await Promise.allSettled([
        invoiceItemService.filter({ invoiceId: id }),
        inv.customerId ? customerService.get(inv.customerId) : Promise.resolve(null),
        // Seller is the tenant's first active company row.
        companyService.all(),
        // Print template for this invoice's store, falling back to the tenant
        // default. Never blocks the render — the document has its own defaults.
        printFormatService.effective({ documentType: 'invoice', storeId: inv.storeId }),
      ]);
      if (tasks[0].status === 'fulfilled' && Array.isArray(tasks[0].value)) {
        setItems(tasks[0].value);
      }
      if (tasks[1].status === 'fulfilled' && tasks[1].value) {
        setBuyer(tasks[1].value as Customer);
      }
      if (tasks[2].status === 'fulfilled' && Array.isArray(tasks[2].value)) {
        const list = tasks[2].value as Company[];
        const first = list.find((c) => c.isActive !== false) ?? list[0];
        if (first) setSeller(first);
      }
      if (tasks[3].status === 'fulfilled' && tasks[3].value) {
        const fmt = tasks[3].value as PrintFormat;
        setFormat(fmt);
        /* Product-name translations for the print language. Fire-and-forget:
           the document renders immediately with stored names and upgrades to
           translated ones when they arrive, so a slow or failed lookup never
           delays the invoice. */
        const lang = fmt.defaultLanguageCode;
        if (lang) {
          entityTranslationService
            .forType('Product', lang)
            .then((rows) => setProductTranslations(translationIndex(rows)))
            .catch(() => { /* untranslated tenant — stored names are correct */ });
        }
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load invoice.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [id]);

  const grandTotal = Number(invoice?.totalAmount ?? 0);

  /**
   * Freeze the document exactly as rendered, then print.
   *
   * The archive is what makes a reprint trustworthy: the page re-renders from
   * live data, so without this a document reissued after a product rename, a
   * tax-rate edit or a template restyle would silently differ from the one the
   * customer received. Capture failure never blocks printing — the user's
   * intent was to print, and the snapshot is a side benefit.
   */
  const captureSnapshot = async () => {
    if (!invoice?.invoiceId || !docRef.current) return;
    try {
      await invoicePrintSnapshotService.issue({
        invoiceId: invoice.invoiceId,
        invoiceNumber: invoice.invoiceNumber ?? '',
        printFormatId: format?.printFormatId,
        renderedHtml: docRef.current.innerHTML,
        totalAmount: Number(invoice.totalAmount ?? 0),
        currencyCode: 'INR',
      });
    } catch {
      /* Archive is best-effort — never block the print the user asked for. */
    }
  };

  const handlePrint = async () => {
    setBusy('print');
    try {
      await captureSnapshot();
      window.print();
    } finally {
      // window.print is synchronous in Chrome; reset immediately.
      setBusy(null);
    }
  };

  const handleDownload = async () => {
    if (!docRef.current) return;
    setBusy('pdf');
    try {
      await exportToPdf(
        docRef.current,
        `Invoice-${invoice?.invoiceNumber ?? id ?? 'KRPL'}`,
      );
      toast.success('Invoice downloaded');
    } catch (e) {
      toast.error('Download failed', e instanceof Error ? e.message : 'Try again.');
    } finally {
      setBusy(null);
    }
  };

  /**
   * Customer-facing link for this invoice.
   *
   * Previously the share used `window.location.href` — the PANEL url, which is
   * behind admin auth. A customer opening it got the login screen, so the share
   * button never actually delivered an invoice. This points at the storefront's
   * `/account/invoices/:id` page instead.
   */
  const shareUrl = invoice?.invoiceId ? invoiceUrl(invoice.invoiceId) : '';
  const shareMessage = [
    `*Invoice ${invoice?.invoiceNumber ?? ''}*`,
    seller?.name ? `From: ${seller.name}` : null,
    buyer?.name ? `Billed to: ${buyer.name}` : null,
    `Amount due: ₹${grandTotal.toLocaleString('en-IN')}`,
    invoice?.dueDate ? `Due by: ${invoice.dueDate.slice(0, 10)}` : null,
  ].filter(Boolean).join('\n');

  return (
    <div className="-m-6 p-[3px]">
      {/* Action toolbar — hidden in print output via the global
          @media print rule below. */}
      <div className="invoice-actions mb-3 flex flex-wrap items-center justify-between gap-2">
        <Button variant="outline" size="sm" onClick={() => navigate('/panel/invoices')}>
          <ArrowLeft className="h-3.5 w-3.5" /> Back
        </Button>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={handlePrint} disabled={!invoice || busy === 'print'}>
            <Printer className="h-3.5 w-3.5" /> Print
          </Button>
          <Button variant="outline" size="sm" onClick={handleDownload} disabled={!invoice || busy === 'pdf'}>
            <Download className="h-3.5 w-3.5" /> {busy === 'pdf' ? 'Downloading…' : 'Download PDF'}
          </Button>
          <ShareMenu
            content={{ title: `Invoice ${invoice?.invoiceNumber ?? ''}`, message: shareMessage, url: shareUrl }}
            label="Share"
            disabled={!invoice || !shareUrl}
            warning={shareUrl && isUnreachableHost(shareUrl) ? 'Link is not publicly reachable' : null}
            onResult={(_c, result) => {
              if (result === 'copied') toast.success('Link copied');
              if (result === 'unsupported') toast.error('Sharing unavailable', 'Try copying the link.');
            }}
          />
        </div>
      </div>

      {error && <Alert variant="danger" className="mb-3">{error}</Alert>}

      {loading ? (
        <Card className="p-6 text-center text-sm text-content-muted">Loading invoice…</Card>
      ) : !invoice ? (
        <Card className="p-6 text-center text-sm text-content-muted">Invoice not found.</Card>
      ) : (
        <div ref={docRef}>
          <InvoiceDocument
            invoice={invoice}
            items={items}
            seller={seller}
            buyer={buyer}
            signatureSrc={
              invoice.signatureImageUrl
                ? toAbsoluteImageUrl(invoice.signatureImageUrl)
                : ''
            }
            format={format}
            /* Print language. The format's own `defaultLanguageCode` is used
               when this is undefined, so a tenant that has set a default gets
               it without the preview having to know. */
            languageCode={format?.defaultLanguageCode}
            productTranslations={productTranslations}
          />
        </div>
      )}

      {/* Print styles — hide everything except the invoice doc and
          force white background / black text. */}
      <style>{`
        @media print {
          .invoice-actions { display: none !important; }
          body { background: #fff !important; }
          .invoice-doc { border: 0 !important; padding: 24px !important; }
          aside, header, footer, nav { display: none !important; }
        }
      `}</style>
    </div>
  );
}
